import { useCallback, useContext, useEffect, useMemo } from "react";
import { useSetModuleFavorite, useSetModuleUnfavorite } from "../../shared/hooks/useModules";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { IModuleProps } from "./Module.interface";
import { Outlet, useLocation, useNavigate, useOutlet, useParams } from "react-router-dom";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import FavoriteButton from "../../components/FavoriteButton/FavoriteButton";
import Placeholder from "./../../shared/assets/imgs/placeholder.svg";
import { getFormattedPath, handleFileDownload, markdownToHtml } from "../../shared/helpers/utils";
import Link from "../../components/Link/Link";
import UnitCard from "../../components/UnitCard/UnitCard";
import { CONTENT_BASE_URL, DSS_SECTION } from "../../shared/constants";
import { routes } from "../../shared/router/routes";
import ModuleCard from "../../components/ModuleCard/ModuleCard";
import Button from "../../components/Button/Button";
import { AnalyticsContext } from "../../shared/providers/analytics/analytics.provider";
import { AuthenticationContext } from "../../shared/providers/authentication/authentication.provider";
import { LayoutContext } from "../../shared/providers/layout/layout.provider";
import { IModule } from "../../shared/interfaces/modules.interface";

function Module(props: IModuleProps) {
    const outlet = useOutlet();
    const location = useLocation();
    const navigate = useNavigate();
    const { translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { pathwayModules, module, setUpdated, setCurrentModuleId, setModule } =
        useContext(ModuleContext);
    const { setEvent } = useContext(AnalyticsContext);
    const { user } = useContext(AuthenticationContext);
    const { setActions, setTitle } = useContext(LayoutContext);
    const { moduleId: id } = useParams();
    const fromExplore = !!(location.state as { fromExplore?: boolean })?.fromExplore;
    const backTo = useMemo(() => {
        const from = (location.state as { from?: string } | null)?.from;
        if (from?.startsWith("/") && !from.startsWith("//")) return from;

        try {
            if (document.referrer) {
                const referrer = new URL(document.referrer);
                if (referrer.origin === window.location.origin) {
                    return `${referrer.pathname}${referrer.search}${referrer.hash}`;
                }
            }
        } catch {
            // ignore invalid referrer
        }

        return routes.EXPLORE.path;
    }, [location.state]);

    const setFavorite = useSetModuleFavorite();
    const setUnfavorite = useSetModuleUnfavorite();
    const hasDocuments = useMemo(
        () => module?.documents && module?.documents?.length > 0,
        [module]
    );
    const mentalHealthDSS = useMemo(
        () => module?.related_dss?.filter(dss => dss.section === DSS_SECTION.MENTAL) ?? [],
        [module]
    );
    const physicalHealthDSS = useMemo(
        () => module?.related_dss?.filter(dss => dss.section === DSS_SECTION.PHYSICAL) ?? [],
        [module]
    );

    const suggestions = useMemo(
        () => (module?.related_modules?.reduce((list: IModule[], id: string) => {

            const module = pathwayModules?.find(mod => mod.id === id);

            if (!module) return list;

            return [...list, module]
        }, [])) ?? []
        ,
        [pathwayModules, module]
    );

    const duration = useMemo(
        () =>
            module?.units?.reduce((unitAcc, unit) => {
                return (unitAcc += unit.items.reduce((itemAcc, item) => {
                    return (itemAcc += !item || !item.duration ? 0 : item.duration);
                }, 0));
            }, 0),
        [module]
    );

    const completedUnits = useMemo(() => {
        return (
            module?.unitsProgress?.reduce((completed, unit) => {
                const numberItems = module?.units?.find((u) => u.id === unit.id)?.items.length ?? 1;

                return (completed += unit.completedItems === numberItems ? 1 : 0);
            }, 0) ?? 0
        );
    }, [module]);

    const nextUnitId = useMemo(() => {
        const [nextUnit] =
            module?.units?.filter((unit) => !unit.isCompleted) ?? module?.units ?? [];

        return nextUnit?.id ?? module?.units?.[0]?.id ?? false;
    }, [module]);

    useEffect(() => {
        setCurrentModuleId(id);
        document.body.scrollIntoView({ behavior: "smooth" })

        return () => {
            setCurrentModuleId(undefined);
            setModule(undefined);
        };
    }, [id]);

    const handleFavorite = async () => {
        if (!id || !module) return;
        setIsLoading(true);

        if (!module.isFavorite) await setFavorite.mutateAsync(id);
        else await setUnfavorite.mutateAsync(id);

        setEvent({
            event_name: "app_favourites",
            event_category: "content_navigation",
            event_action: "app_favourites",
            user_token: user?.id,
            module_id: module.id
        });

        setUpdated(new Date());
        setIsLoading(false);
    };

    const handleBack = useCallback(
        (event: any) => {
            const historyIdx = (window.history.state as { idx?: number } | null)?.idx;
            const hasInAppHistory = typeof historyIdx === "number" && historyIdx > 0;

            let sameOriginReferrer = false;
            try {
                sameOriginReferrer =
                    !!document.referrer &&
                    new URL(document.referrer).origin === window.location.origin;
            } catch {
                sameOriginReferrer = false;
            }

            if (window.history.length > 1 && (hasInAppHistory || sameOriginReferrer)) {
                event.preventDefault();
                navigate(-1);
            }
        },
        [navigate]
    );

    useEffect(() => {
        if (outlet) return;

        setActions(<Link
            to={routes.EXPLORE.path}
            onClick={handleBack}
            icon="Chevron--left"
            iconSize={16}
            customCss="p-0 ms-lg-2 d-flex align-items-center gap-01 color-neutrals-black text-bd2-rg">
            {translate("common.back", "Back")}
        </Link>)

        return () => {
            setActions(undefined)
        }
    }, [translate, setActions, outlet, handleBack]);

    useEffect(() => {
        if (!module) return;
        setTitle(module?.title);
    }, [module])

    return (
        <>
            {!outlet && (
                <div className="container d-flex flex-column p-11 p-md-2 px-lg-0 mb-md-5 pb-1">
                    <section className="module d-flex flex-column gap-11">
                        <div className="d-flex flex-column gap-02">
                            <div className="d-flex justify-content-between">
                                <span
                                    className={`color-${module?.tag_color ?? "yellow"}-500 text-bd4-bd text-uppercase`}>
                                    {module?.tag}
                                </span>
                                <FavoriteButton
                                    className="ms-auto"
                                    isFavorite={module?.isFavorite}
                                    onClick={handleFavorite}
                                />
                            </div>
                            <h1 className="text-h1-bd">{module?.title}</h1>
                            <div className="d-flex justify-content-between">
                                <span className="d-flex gap-01 align-items-center color-neutrals-dark text-bd3-rg">
                                    <i className={`icon icon-16 icon-Time`}></i>
                                    {duration}
                                    min {translate("common.article", "article")}
                                </span>
                                {module?.units && (
                                    <span className="color-neutrals-dark text-bd3-rg">
                                        {`${!module?.unitsProgress ? 0 : completedUnits} / ${module?.units?.length} ${module?.units?.length > 1
                                            ? translate("common.units", "units")
                                            : translate("common.unit", "unit")
                                            }`}
                                    </span>
                                )}
                            </div>
                        </div>

                        {module?.description && <div className="mt-0 module_description color-neutrals-black text-bd1-rg rich_text"
                            dangerouslySetInnerHTML={{
                                __html: markdownToHtml(module?.description ?? "")
                            }}
                        />}
                        <div className="d-flex flex-column gap-2">
                            <ul className="d-flex flex-column gap-02">
                                {module?.units?.map((unit, i) => (
                                    <li key={i}>
                                        <UnitCard
                                            progress={module.unitsProgress?.find(
                                                (u) => u.id === unit.id
                                            )}
                                            unit={unit}
                                            index={i + 1}
                                            moduleId={module.id}
                                            moduleImage={
                                                module.image?.url
                                                    ? `${CONTENT_BASE_URL}${module.image?.url}`
                                                    : Placeholder
                                            }
                                        />
                                    </li>
                                ))}
                            </ul>
                            {module?.moduleProgress !== 100 && <Button
                                type="button"
                                variant="solid"
                                customCss="w-100"
                                onClick={() =>
                                    navigate(
                                        getFormattedPath(routes.UNIT.path, {
                                            moduleId: module?.id,
                                            unitId: nextUnitId
                                        }),
                                        {
                                            state: fromExplore
                                                ? { fromExplore: true }
                                                : undefined
                                        }
                                    )
                                }
                                label={
                                    module?.moduleProgress
                                        ? translate(
                                            "module.continue_module",
                                            "Continue module"
                                        )
                                        : translate("module.start_module", "Start module")
                                }
                            />}
                        </div>
                    </section>
                    {hasDocuments && (
                        <section className="py-2" aria-labelledby="documents">
                            <h2 className="text-h3-smbd mb-12" id="documents">
                                {translate(
                                    "modules.support_materials",
                                    "Support Materials"
                                )}
                            </h2>
                            <div className="card card_documents bg-neutrals-white d-flex flex-column gap-02 p-1">
                                <h3 className="text-h5-smbd mb-12">
                                    {translate("common.documents", "Documents")}
                                </h3>
                                <ul>
                                    {module?.documents?.map((doc, i) => (
                                        <li key={i} className="card_document d-flex">
                                            <div className="d-flex flex-wrap gap-02 w-100 py-03">
                                                <Button
                                                    type="button"
                                                    onClick={() =>
                                                        handleFileDownload(
                                                            `${CONTENT_BASE_URL}${doc.url}`,
                                                            doc.alt ?? doc.name
                                                        )
                                                    }
                                                    customCss="justify-content-start button card_title text-h5-smbd w-100 p-0  ">
                                                    {doc.alt ?? doc.name}
                                                </Button>
                                                <span className="color-neutrals-neutral text-bd3-rg text-uppercase">
                                                    {doc.ext?.replace(".", "")}
                                                </span>
                                                <span className="color-neutrals-neutral text-bd3-rg text-uppercase">
                                                    {Math.round(doc.size)}KB
                                                </span>
                                            </div>
                                            <Button
                                                type="button"
                                                icon="Download"
                                                iconSize={24}
                                                onClick={() =>
                                                    handleFileDownload(
                                                        `${CONTENT_BASE_URL}${doc.url}`,
                                                        doc.alt ?? doc.name
                                                    )
                                                }
                                                customCss="justify-content-start button card_title text-h5-smbd p-0"></Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>
                    )}

                    {!!module?.related_dss?.length && (
                        <section className="py-2" aria-labelledby="decision_support_system_forms">

                            <h2 className="text-h3-smbd mb-12" id="decision_support_system_forms">
                                {translate(
                                    "modules.evaluate_company",
                                    "Evaluate your company"
                                )}
                            </h2>
                            {mentalHealthDSS.length > 0 && <><h3 className="d-flex gap-02 text-bd2-bd text-uppercase color-blue-500 mt-12 mb-1">
                                <i className="icon icon-24 icon-neurology" aria-hidden="true"></i>
                                Mental health
                            </h3>
                                <ul className="d-flex flex-column gap-1">
                                    {mentalHealthDSS.map((dss, i) => (
                                        <li key={i} className="bg-neutrals-white card card_related_dss d-flex">
                                            <div className="d-flex gap-12 w-100 p-13 align-items-center flex-wrap flex-md-nowrap">
                                                <h3 className="text-h3-md">{dss.title}</h3>
                                                <Link
                                                    to={getFormattedPath(routes.DSS_FORM.path, { formId: dss.formId })}
                                                    variant="solid"
                                                    customCss="ms-auto flex-md-grow-0 flex-md-shrink-0 flex-grow-1"
                                                    label={translate("dss.assess_your_workplace", "Assess your workplace")}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </>}

                            {physicalHealthDSS.length > 0 && <><h3 className="d-flex gap-02 text-bd2-bd text-uppercase color-blue-500 mt-12 mb-1">
                                <i className="icon icon-24 icon-physical_therapy" aria-hidden="true"></i>
                                Physical health
                            </h3>
                                <ul className="d-flex flex-column gap-1">
                                    {physicalHealthDSS.map((dss, i) => (
                                        <li key={i} className="bg-neutrals-white card card_related_dss d-flex">
                                            <div className="d-flex gap-12 w-100 p-13 align-items-center flex-wrap flex-md-nowrap">
                                                <h3 className="text-h3-md">{dss.title}</h3>
                                                <Link
                                                    to={getFormattedPath(routes.DSS_FORM.path, { formId: dss.formId })}
                                                    variant="solid"
                                                    customCss="ms-auto flex-md-grow-0 flex-md-shrink-0 flex-grow-1"
                                                    label={translate("dss.assess_your_workplace", "Assess your workplace")}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </>}
                        </section>
                    )}
                </div >
            )
            }

            {outlet && <Outlet />}

            {
                suggestions && suggestions?.length > 0 && (
                    <div className="bg-blue-50 w-100 py-61 px-31 mt-auto d-none d-lg-block">
                        <h2 className="text-h4-md mb-12">
                            {translate("common.recommendations", "Recommendations")}
                        </h2>
                        <div className="suggestions d-flex gap-12">
                            {suggestions
                                ?.filter((_, i) => i < 5)
                                .map((mod, i) => (
                                    <ModuleCard key={i} {...mod} fromModule />
                                ))}
                        </div>
                    </div>
                )
            }
            {
                suggestions && suggestions?.length > 0 && (
                    <div className="bg-blue-50 w-100 px-11 py-22 py-md-61 px-md-31 mt-auto d-lg-none">
                        <div className="d-flex justify-content-between align-items-center mb-12">
                            <h2 className="text-h4-md ">
                                {translate("common.recommendations", "Recommendations")}
                            </h2>
                            <Link
                                to={routes.EXPLORE.path}
                                variant="link"
                                icon="Chevron--right"
                                isIconRight
                                customCss="color-green-600">
                                {translate("common.view_all", "View all")}
                            </Link>
                        </div>
                        <div className="d-flex flex-column gap-02 gap-md-12">
                            {suggestions
                                ?.filter((_, i) => i < 3)
                                .map((mod, i) => (
                                    <ModuleCard key={i} {...mod} isSmall fromModule />
                                ))}
                        </div>
                    </div>
                )
            }
        </>
    );
}

export default Module;
